import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('accessToken'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));

  // Initialize authentication on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const accessToken = sessionStorage.getItem('accessToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (accessToken && storedRefreshToken) {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/auth/me`,
            {
              headers: { Authorization: `Bearer ${accessToken}` }
            }
          );
          setUser(response.data);
          setToken(accessToken);
          setRefreshToken(storedRefreshToken);
        } catch (err) {
          // Try to refresh token
          if (storedRefreshToken) {
            try {
              await refreshAccessToken(storedRefreshToken);
            } catch {
              sessionStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              setToken(null);
              setRefreshToken(null);
            }
          }
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const register = async (email, username, password, fullName) => {
    try {
      setError(null);
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        {
          email,
          username,
          password,
          full_name: fullName
        }
      );

      const { access_token, refresh_token, user: userData } = response.data;
      
      sessionStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      
      setToken(access_token);
      setRefreshToken(refresh_token);
      setUser(userData);
      
      return userData;
    } catch (err) {
      // Handle Pydantic validation errors (array of error objects)
      let message = 'Registration failed';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Extract user-friendly messages from validation errors
          message = err.response.data.detail
            .map(error => error.msg || error.message || 'Validation error')
            .join('. ');
        } else {
          // Handle simple string errors
          message = err.response.data.detail;
        }
      }
      setError(message);
      throw new Error(message);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        new URLSearchParams({
          username: email,
          password: password
        }),
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }
      );

      const { access_token, refresh_token, user: userData } = response.data;
      
      sessionStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      
      setToken(access_token);
      setRefreshToken(refresh_token);
      setUser(userData);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = () => {
    sessionStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setError(null);
  };

  const refreshAccessToken = async (storedRefreshToken) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/refresh`,
        { refresh_token: storedRefreshToken || refreshToken }
      );

      const { access_token, refresh_token, user: userData } = response.data;
      
      sessionStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      
      setToken(access_token);
      setRefreshToken(refresh_token);
      setUser(userData);
      
      return access_token;
    } catch (err) {
      logout();
      throw err;
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/password-reset-request`,
        { email }
      );
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Password reset request failed');
    }
  };

  const confirmPasswordReset = async (token, newPassword) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/password-reset-confirm`,
        { token, new_password: newPassword }
      );
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Password reset failed');
    }
  };

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      error,
      token,
      refreshToken,
      register,
      login,
      logout,
      refreshAccessToken,
      requestPasswordReset,
      confirmPasswordReset,
      isAuthenticated: !!user
    }),
    [user, loading, error, token, refreshToken]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
